import { useCallback, useMemo } from "react";
import { useFormatter } from "use-intl";

import { useMinuteNow } from "@/hooks/use-minute-now";

type DateValue = Date | string | number | null | undefined;
type AppDateTimeOptions = {
  dateStyle?: "short" | "medium" | "long";
  timeStyle?: "short";
  day?: "2-digit" | "numeric";
  hour?: "numeric";
  minute?: "2-digit";
  month?: "2-digit" | "short" | "long";
  year?: "numeric";
};
type UseAppDateTimeOptions = {
  timeZone?: string;
  /** Keep relative times current with a shared minute clock instead of the request-time `now`. */
  live?: boolean;
};

function validDate(value: DateValue) {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function useAppDateTime(options: UseAppDateTimeOptions = {}) {
  const format = useFormatter();
  const { timeZone, live = false } = options;
  const now = useMinuteNow(live);
  const render = useCallback(
    (value: DateValue, options: AppDateTimeOptions, fallback = "—") => {
      const date = validDate(value);
      return date ? format.dateTime(date, { ...options, timeZone }) : fallback;
    },
    [format, timeZone],
  );
  const date = useCallback(
    (value: DateValue, fallback?: string) => render(value, { dateStyle: "medium" }, fallback),
    [render],
  );
  const longDate = useCallback(
    (value: DateValue, fallback?: string) => render(value, { dateStyle: "long" }, fallback),
    [render],
  );
  const shortDate = useCallback(
    (value: DateValue, fallback?: string) => render(value, { dateStyle: "short" }, fallback),
    [render],
  );
  const dateTime = useCallback(
    (value: DateValue, fallback?: string) =>
      render(value, { dateStyle: "medium", timeStyle: "short" }, fallback),
    [render],
  );
  const longDateTime = useCallback(
    (value: DateValue, fallback?: string) =>
      render(value, { dateStyle: "long", timeStyle: "short" }, fallback),
    [render],
  );
  const compactDate = useCallback(
    (value: DateValue, fallback?: string) =>
      render(value, { day: "2-digit", month: "2-digit" }, fallback),
    [render],
  );
  const monthDay = useCallback(
    (value: DateValue, fallback?: string) =>
      render(value, { day: "numeric", month: "short" }, fallback),
    [render],
  );
  /** A key that is equal for two instants on the same calendar day in the viewer's time zone. */
  const calendarDayKey = useCallback(
    (value: DateValue) => render(value, { year: "numeric", month: "2-digit", day: "2-digit" }),
    [render],
  );
  const monthYear = useCallback(
    (value: DateValue, fallback?: string) =>
      render(value, { month: "long", year: "numeric" }, fallback),
    [render],
  );
  const time = useCallback(
    (value: DateValue, fallback?: string) =>
      render(value, { hour: "numeric", minute: "2-digit" }, fallback),
    [render],
  );
  const timeAgo = useCallback(
    (value: DateValue, fallback = "—") => {
      const parsed = validDate(value);
      if (!parsed) return fallback;
      const elapsed = parsed.getTime() - now.getTime();
      if (Math.abs(elapsed) >= 7 * 86_400_000) return date(parsed, fallback);
      return format.relativeTime(parsed, { now });
    },
    [date, format, now],
  );
  const recentTimeAgo = useCallback(
    (value: DateValue, fallback = "—") => {
      const parsed = validDate(value);
      if (!parsed) return fallback;
      const elapsed = parsed.getTime() - now.getTime();
      if (elapsed <= -86_400_000 || elapsed > 0) return monthDay(parsed, fallback);
      return format.relativeTime(parsed, { now });
    },
    [format, monthDay, now],
  );
  const recentTime = useCallback(
    (value: DateValue, fallback = "—") => {
      const parsed = validDate(value);
      if (!parsed) return null;
      const elapsed = parsed.getTime() - now.getTime();
      const relative = elapsed > -86_400_000 && elapsed <= 0;
      return {
        kind: relative ? ("relative" as const) : ("absolute" as const),
        value: relative ? recentTimeAgo(parsed, fallback) : monthDay(parsed, fallback),
      };
    },
    [monthDay, now, recentTimeAgo],
  );
  const timeAgoCompact = useCallback(
    (value: DateValue, fallback = "—") => {
      const parsed = validDate(value);
      if (!parsed) return fallback;
      const elapsed = parsed.getTime() - now.getTime();
      if (Math.abs(elapsed) >= 7 * 86_400_000) return shortDate(parsed, fallback);
      return format.relativeTime(parsed, { now, style: "narrow" });
    },
    [format, now, shortDate],
  );

  return useMemo(
    () => ({
      calendarDayKey,
      compactDate,
      date,
      dateTime,
      longDate,
      longDateTime,
      monthDay,
      monthYear,
      recentTime,
      recentTimeAgo,
      shortDate,
      time,
      timeAgo,
      timeAgoCompact,
    }),
    [
      calendarDayKey,
      compactDate,
      date,
      dateTime,
      longDate,
      longDateTime,
      monthDay,
      monthYear,
      recentTime,
      recentTimeAgo,
      shortDate,
      time,
      timeAgo,
      timeAgoCompact,
    ],
  );
}
